import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Sparkles, Loader2, BookOpen } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { usePrefs } from "@/lib/context";
import { fetcher, useCategories, catName } from "@/lib/hooks";
import { Field } from "@/components/PropertyDialog";
import { PhotoUpload } from "@/components/PhotoUpload";
import { StatusBadge } from "@/components/common";

const URG = ["basse", "normale", "haute", "urgente"];
const EMPTY = { title: "", description: "", property_id: "", unit_id: "", category: "autre", urgency: "normale", photos: [] };

export function AiCard({ s }) {
  const { t } = usePrefs();
  const { label } = useCategories();
  return (
    <div className="rounded-sm border border-[#e6d39f] bg-gradient-to-br from-[#fdf8ec] to-white p-4" data-testid="ai-suggestion-card">
      <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.14em] text-[#996515]"><Sparkles size={14} />{t("aiSuggestion")}</div>
      <div className="mb-2 flex flex-wrap items-center gap-2 text-sm"><span className="font-semibold" data-testid="ai-category">{label(s.category)}</span><StatusBadge value={s.urgency} testId="ai-urgency" /></div>
      <p className="text-sm text-slate-700" data-testid="ai-solution">{s.solution}</p>
      {s.faq_title && <div className="mt-2 flex items-center gap-1 text-xs text-slate-500"><BookOpen size={12} />{s.faq_title}</div>}
    </div>
  );
}

export function TicketDialog({ trigger, tenant }) {
  const { t, lang } = usePrefs();
  const cats = useCategories();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(EMPTY);
  const [ai, setAi] = useState(null);
  const [busy, setBusy] = useState(false);
  const { data: props = [] } = useQuery({ queryKey: ["ticket-props", tenant], enabled: open,
    queryFn: async () => tenant ? (await fetcher("/leases")).map((l) => ({ id: l.property_id, name: `${l.property.name} · ${l.unit.reference}` })) : fetcher("/properties") });
  const { data: prop } = useQuery({ queryKey: ["property", f.property_id], queryFn: () => fetcher(`/properties/${f.property_id}`), enabled: !!f.property_id });
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const analyze = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/tickets/ai-suggest", { title: f.title, description: f.description });
      setAi(data);
      setF((x) => ({ ...x, category: data.category, urgency: data.urgency }));
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const save = async () => {
    try {
      await api.post("/tickets", { ...f, unit_id: f.unit_id || null, ai_suggestion: ai });
      toast.success(t("newTicket"));
      qc.invalidateQueries({ queryKey: ["tickets"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      setOpen(false); setF(EMPTY); setAi(null);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-h-[92vh] max-w-xl overflow-y-auto rounded-sm" data-testid="ticket-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{t("newTicket")}</DialogTitle></DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={t("property")}>
            <Select value={f.property_id} onValueChange={(v) => setF({ ...f, property_id: v, unit_id: "" })}>
              <SelectTrigger data-testid="ticket-property-select"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>{props.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label={t("unit")}>
            <Select value={f.unit_id} onValueChange={set("unit_id")} disabled={!prop}>
              <SelectTrigger data-testid="ticket-unit-select"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>{(prop?.units || []).map((u) => <SelectItem key={u.id} value={u.id}>{u.reference} {u.is_common ? `· ${u.type}` : ""}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <div className="sm:col-span-2"><Field label={t("title")}><Input value={f.title} onChange={set("title")} data-testid="ticket-title-input" /></Field></div>
          <div className="sm:col-span-2"><Field label={t("description")}><Textarea rows={4} value={f.description} onChange={set("description")} data-testid="ticket-description-input" /></Field></div>
          <div className="sm:col-span-2">
            <button type="button" onClick={analyze} disabled={busy || !f.title || !f.description} data-testid="ai-analyze-button"
              className="flex items-center gap-2 rounded-sm border border-[#B8860B] px-3 py-2 text-sm font-semibold text-[#996515] transition-colors hover:bg-[#B8860B] hover:text-white disabled:opacity-40">
              {busy ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />}{t("aiAnalyze")}
            </button>
          </div>
          {ai && <div className="sm:col-span-2"><AiCard s={ai} /></div>}
          <Field label={t("category")}>
            <Select value={f.category} onValueChange={set("category")}>
              <SelectTrigger data-testid="ticket-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>{cats.trade.map((c) => <SelectItem key={c.key} value={c.key}>{catName(c, lang)}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label={t("urgency")}>
            <Select value={f.urgency} onValueChange={set("urgency")}>
              <SelectTrigger data-testid="ticket-urgency-select"><SelectValue /></SelectTrigger>
              <SelectContent>{URG.map((u) => <SelectItem key={u} value={u}>{t(u)}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <div className="sm:col-span-2"><Field label={t("photos")}><PhotoUpload value={f.photos} onChange={set("photos")} testId="ticket-photo-upload" /></Field></div>
        </div>
        <Button onClick={save} disabled={!f.title || !f.description || !f.property_id} className="rounded-sm bg-[#0F172A] hover:bg-[#B8860B]" data-testid="ticket-save-button">{t("create")}</Button>
      </DialogContent>
    </Dialog>
  );
}
